import React, { useEffect, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
} from "chart.js";
import axios from "axios";
import { Card, Statistic, Row, Col, Typography, Spin } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  MailOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import CountUp from "react-countup";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement
);

const { Title: AntTitle } = Typography;

const Dashboard = () => {
  const [greetingCardsData, setGreetingCardsData] = useState([]);
  const [orderData, setOrderData] = useState({
    totalAwaited: 0,
    totalPrinted: 0,
    totalMailed: 0,
  });
  const [eventsData, setEventsData] = useState([]);
  const [statistics, setStatistics] = useState({
    totalCards: 0,
    totalEvents: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardAndOrderData();
  }, []);

  const fetchData = async (url, params = {}) => {
    try {
      const response = await axios.get(url, {
        headers: { "x-sh-auth": localStorage.getItem("token") },
        params,
      });
      return response.data.code === 200 ? response.data.data : null;
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
      return null;
    }
  };

  const fetchDashboardAndOrderData = async () => {
    setLoading(true);
    try {
      const [
        cardsCompleted,
        cardsPrinted,
        cardsMailed,
        cardsResponse,
        eventsResponse,
        statsResponse,
      ] = await Promise.allSettled([
        fetchData(
          `${process.env.REACT_APP_API_URL}/eventOfRecipient/get-all-customer-cards`,
          { page: 1, limit: 1000, status: "completed" }
        ),
        fetchData(
          `${process.env.REACT_APP_API_URL}/eventOfRecipient/get-all-customer-cards`,
          { page: 1, limit: 1000, status: "printed", admin: true }
        ),
        fetchData(
          `${process.env.REACT_APP_API_URL}/eventOfRecipient/get-all-customer-cards`,
          { page: 1, limit: 1000, status: "mailed" }
        ),
        fetchData(`${process.env.REACT_APP_API_URL}/card/get-cards-for-admin`),
        fetchData(`${process.env.REACT_APP_API_URL}/event/get-active-event`, {
          admin: true,
        }),
        fetchData(
          `${process.env.REACT_APP_API_URL}/customer/get-all-customers`
        ),
      ]);

      setOrderData((prev) => ({
        totalAwaited: cardsCompleted?.value?.length || 0,
        totalPrinted: cardsPrinted?.value?.length || 0,
        totalMailed: cardsMailed?.value?.length || 0,
      }));

      if (cardsResponse.status === "fulfilled" && cardsResponse.value) {
        setGreetingCardsData(cardsResponse.value);
        setStatistics((prev) => ({
          ...prev,
          totalCards: cardsResponse.value.length,
        }));
      }

      if (eventsResponse.status === "fulfilled" && eventsResponse.value) {
        setEventsData(eventsResponse.value);
        setStatistics((prev) => ({
          ...prev,
          totalEvents: eventsResponse.value.length,
        }));
      }

      if (statsResponse.status === "fulfilled" && statsResponse.value) {
        setStatistics((prev) => ({
          ...prev,
          totalUsers: statsResponse.value.length,
        }));
      }
    } catch (error) {
      console.error("Error fetching dashboard and order data:", error);
    } finally {
      setLoading(false);
    }
  };
  const greetingCardsChartData = {
    // labels: greetingCardsData.map((card) =>
    //   new Date(card.eventDate).toLocaleDateString()
    // ),
    // datasets: [
    //   {
    //     label: "Greeting Cards",
    //     data: greetingCardsData.map((card) => 100),
    //     backgroundColor: "rgba(75, 192, 192, 0.6)",
    //     borderColor: "rgba(75, 192, 192, 1)",
    //     borderWidth: 1,
    //   },
    // ],

    labels: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    datasets: [
      {
        label: "Draft",
        data: [30, 20, 15, 35, 40, 45, 30, 25, 20, 15, 10, 5],
        backgroundColor: "rgba(255, 99, 132, 0.5)", // Pink/Red color for Draft
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
      {
        label: "Mailed",
        data: [20, 15, 25, 10, 30, 20, 35, 40, 30, 20, 25, 30],
        backgroundColor: "rgba(54, 162, 235, 0.5)", // Blue color for Mailed
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Pending",
        data: [10, 25, 20, 15, 10, 5, 20, 15, 10, 25, 30, 20],
        backgroundColor: "rgba(255, 206, 86, 0.5)", // Yellow color for Pending
        borderColor: "rgba(255, 206, 86, 1)",
        borderWidth: 1,
      },
      {
        label: "Completed",
        data: [5, 10, 20, 25, 15, 20, 10, 30, 35, 40, 45, 50],
        backgroundColor: "rgba(75, 192, 192, 0.5)", // Green color for Completed
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  console.log({ greetingCardsChartData });

  const eventsChartData = {
    labels: eventsData.map((event) => event.name),
    datasets: [
      {
        label: "Events",
        data: eventsData.map((event) => event.eventCounts),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };

  const pieChartData = {
    labels: ["Total Cards", "Total Events", "Total Users"],
    datasets: [
      {
        data: [
          statistics.totalCards,
          statistics.totalEvents,
          statistics.totalUsers,
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 205, 86, 0.6)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 205, 86, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const statItems = [
    {
      title: "Total Users",
      value: statistics.totalUsers,
      icon: <UserOutlined className="text-primary text-2xl mr-2" />,
      color: "text-primary",
    },
    {
      title: "Total Events",
      value: eventsData.length,
      icon: <CalendarOutlined className="text-blue-500 text-2xl mr-2" />,
      color: "text-blue-500",
    },
    {
      title: "Total Greeting Cards",
      value: greetingCardsData.length,
      icon: <FileImageOutlined className="text-green-500 text-2xl mr-2" />,
      color: "text-green-500",
    },
    {
      title: "Total Order Mailed",
      value: orderData.totalMailed || 0,
      icon: <FileImageOutlined className="text-green-600 text-2xl mr-2" />,
      color: "bg-green-100",
    },
    {
      title: "Total Order Printed, But Not Mailed Yet!",
      value: orderData.totalPrinted || 0,
      icon: <FileImageOutlined className="text-blue-600 text-2xl mr-2" />,
      color: "bg-blue-100",
    },
    {
      title: "Total Order Pending",
      value: orderData.totalAwaited || 0,
      icon: <FileImageOutlined className="text-yellow-600 text-2xl mr-2" />,
      color: "bg-yellow-100",
    },
  ];

  return (
    <div className="dashboard-container">
      <AntTitle level={2} className="text-center mb-6">
        Admin Dashboard
      </AntTitle>
      {/* {loading ? (
        <div className="text-center">
          <Spin size="large" />
        </div>
      ) : (
        <> */}
      <Row gutter={[16, 16]} className="mb-6">
        {statItems.map((stat, index) => (
          <Col xs={24} sm={12} md={8} key={index}>
            <Card
              className={`transition transform hover:border-primary hover:shadow-md hover:scale-95 border border-gray-200 rounded-md p-4 ${stat.color}`}
            >
              <div className="flex items-center">
                <div>
                  <h2 className="text-sm font-semibold mb-1">{stat.title}</h2>
                  <div className="flex items-center text-3xl font-bold">
                    {stat.icon}
                    <CountUp
                      start={0}
                      end={stat.value}
                      duration={2}
                      separator=","
                    />
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]} className="mb-6">
      <Col
        xs={24}  // Full width on extra-small screens
        sm={12}  // Half width on small screens
        md={12}  // Half width on medium screens
      >
        <Card
          title="Greeting Cards Over Time"
          className="transition transform hover:border-primary hover:shadow-md hover:scale-80 border border-gray-200 rounded-md"
        >
          {loading ? (
            <div className="text-center min-h-[300px] flex justify-center items-center">
              <Spin size="large" />
            </div>
          ) : (
            <Bar data={greetingCardsChartData} />
          )}
        </Card>
      </Col>

      <Col
        xs={24}  // Full width on extra-small screens
        sm={12}  // Half width on small screens
        md={12}  // Half width on medium screens
      >
        <Card
          title="Events Overview"
          className="transition transform hover:border-primary hover:shadow-md hover:scale-80 border border-gray-200 rounded-md"
        >
          {loading ? (
            <div className="text-center min-h-[300px] flex justify-center items-center">
              <Spin size="large" />
            </div>
          ) : (
            <Bar data={eventsChartData} />
          )}
        </Card>
      </Col>
    </Row>
      <div className="flex justify-center">
        <Row gutter={16} className="w-full sm:w-2/3 md:w-1/2 lg:w-1/3">
          <Col span={24}>
            <Card
              title="Overall Statistics"
              className="transition transform hover:border-primary hover:shadow-md hover:scale-95 border border-gray-200 rounded-md"
            >
              {loading ? (
                <div className="text-center min-h-[300px] flex justify-center items-center">
                  <Spin size="large" />
                </div>
              ) : (
                <div className="flex justify-center">
                  <Pie data={pieChartData} />
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      {/* </>
      )} */}
    </div>
  );
};

export default Dashboard;
